
/**
 * This file sets up the tRPC client for the frontend application.
 * It exports a configured tRPC client that can be used to make type-safe API calls.
 */

import { createTRPCReact, httpBatchLink, splitLink, createWSClient, wsLink } from '@trpc/react-query';
import { QueryClient } from '@tanstack/react-query';
import type { AppRouter } from '@smart-city/api-server/app-router';

// Create React Query client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Helper to get the base URL for API calls
 */
export const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    // In the browser, check if we're running locally or in production
    const isLocal = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.startsWith('192.168.');

    if (isLocal) {
      // For local development, use localhost API
      return 'http://localhost:3001';
    }

    // For SCS production and Cloudflare Pages previews, use the SCS API subdomain.
    if (window.location.hostname.includes('itms.solutions')) {
      return 'https://scsapi.itms.solutions';
    }

    // For Cloudflare Pages preview deployments, also use production API
    if (window.location.hostname.endsWith('.pages.dev')) {
      return 'https://scsapi.itms.solutions';
    }

    // Fallback: use relative URL (same domain)
    return '';
  }

  // When running on the server, use the API_URL from environment
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Default to localhost:3001 for local development
  return 'http://localhost:3001';
};

/**
 * Helper to get WebSocket URL
 */
export const getWsUrl = () => {
  if (typeof window !== 'undefined') {
    // In the browser, check if we're running locally or in production
    const isLocal = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.startsWith('192.168.');

    if (isLocal) {
      // For local development, always connect to localhost:3001
      return 'ws://localhost:3001/api/trpc/ws';
    }

    // For SCS production and Cloudflare Pages previews, use API subdomain.
    if (window.location.hostname.includes('itms.solutions') ||
        window.location.hostname.endsWith('.pages.dev')) {
      return 'wss://scsapi.itms.solutions/ws';
    }

    // Fallback: use the current host
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  }

  // Server-side WebSocket URL
  const baseUrl = import.meta.env.VITE_API_URL || `http://localhost:3001`;
  return baseUrl.replace(/^http/, 'ws') + '/ws';
};

/**
 * Custom transformer for tRPC that is compatible with superjson
 */
export const transformer = {
  serialize: (object: unknown) => {
    return JSON.stringify(object);
  },
  deserialize: (text: string) => {
    return JSON.parse(text);
  },
};

// WebSocket reconnection configuration
const RECONNECT_DELAY_BASE = 1000; // 1 second
const RECONNECT_DELAY_MAX = 30000; // 30 seconds
const RECONNECT_MAX_ATTEMPTS = 10;

let reconnectAttempts = 0;
let reconnectTimer: NodeJS.Timeout | null = null;

// Helper to get WebSocket URL with authentication
const getAuthenticatedWsUrl = () => {
  const baseUrl = getWsUrl();
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('smartcity_auth_token') || 
                  localStorage.getItem('auth-token');
    if (token) {
      // Add token as query parameter for WebSocket authentication
      const url = new URL(baseUrl);
      url.searchParams.set('token', token);
      return url.toString();
    }
  }
  return baseUrl;
};

// Create WebSocket client (only in browser)
const wsClient = typeof window !== 'undefined' ? createWSClient({
  url: getAuthenticatedWsUrl(),
  onOpen: () => {
    console.log('[tRPC] WebSocket connection opened to:', getWsUrl());
    reconnectAttempts = 0; // Reset attempts on successful connection
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  },
  onClose: (cause) => {
    console.log('[tRPC] WebSocket connection closed', cause);
    
    // Attempt reconnection with exponential backoff
    if (reconnectAttempts < RECONNECT_MAX_ATTEMPTS) {
      const delay = Math.min(
        RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttempts),
        RECONNECT_DELAY_MAX
      );
      
      console.log(`[tRPC] Attempting reconnection in ${delay}ms (attempt ${reconnectAttempts + 1}/${RECONNECT_MAX_ATTEMPTS})`);
      
      reconnectTimer = setTimeout(() => {
        reconnectAttempts++;
        // The WebSocket client should automatically attempt to reconnect
        // If not, we might need to recreate the client
      }, delay);
    } else {
      console.error('[tRPC] Max reconnection attempts reached');
    }
  },
  // onError is handled internally by the WebSocket client
}) : null;

/**
 * Create React hooks-compatible tRPC client
 */
export const trpc = createTRPCReact<AppRouter>();
export const api = trpc;

// HTTP headers getter function
const getHeaders = () => {
  const token = typeof window !== 'undefined' ? 
    localStorage.getItem('smartcity_auth_token') || 
    localStorage.getItem('auth-token') : 
    null;
  
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'x-trpc-source': 'react',
  };
};

/**
 * Create a tRPC client instance configuration
 */
export const trpcClientConfig = {
  transformer,
  links: [
    // Use split link to route subscriptions through WebSocket
    typeof window !== 'undefined' && wsClient
      ? splitLink({
          condition(op) {
            // Route subscriptions through WebSocket
            return op.type === 'subscription';
          },
          true: wsLink({
            client: wsClient,
          }),
          false: httpBatchLink({
            url: `${getBaseUrl()}/api/trpc`,
            headers: getHeaders,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: 'include',
              });
            },
          }),
        })
      : httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          headers: getHeaders,
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
            });
          },
        }),
  ],
};

export default trpc; 
